/**
 * Shared presentational component for editable encounter fields.
 * Used by both InlineAttendanceConfirm (briefing) and BulkCompleteDialog (calendar).
 *
 * Shows appointment type, duration, CPT code dropdowns and fee with breakdown.
 * Fee auto-recalculates when type/duration change via fee-preview endpoint.
 */
import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Select,
  MenuItem,
  ListSubheader,
  Divider,
  TextField,
  CircularProgress,
  type SelectChangeEvent,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useEncounterEditFields } from '../hooks/useEncounterEditFields';
import OfferedAppointmentTypesModal from '../../my-settings/OfferedAppointmentTypesModal';
import {
  granularCategoryLabel,
  granularCategoryOrder,
} from '../../../utils/appointmentFormUtils';

interface EncounterEditFieldsProps {
  appointmentTypeId?: string | null;
  durationMinutes?: number | null;
  cptCodeId?: string | null;
  /** Backend final session fee; null/undefined renders as "Fee not set". */
  initialFee?: number | null;
  patientId?: string | null;
  practitionerId?: string | null;
  /** Provider display name for the offered-types modal title (when known). */
  providerName?: string | null;
  caseId?: string | null;
  /** Existing admin/materials fee for display breakdown */
  adminFee?: number;
  adminFeeDescription?: string | null;
  /** Payment status indicators */
  isPaymentOnFile?: boolean;
  paymentType?: string | null;
  splitPaymentEnabled?: boolean;
  /** Callback when any field changes — passes current state for parent sync */
  onChange?: (state: {
    appointmentTypeId: string | null;
    durationMinutes: number | null;
    cptCodeId: string | null;
    fee: number | null;
    feeOverridden: boolean;
  }) => void;
  /** Compact mode for inline chip vs full-width dialog */
  compact?: boolean;
  /** Single horizontal wrapping row of fields (used by BulkCompleteDialog) */
  inline?: boolean;
  /** Extra chips rendered next to the payment-status chips (inline mode) */
  paymentExtras?: React.ReactNode;
  /** Defer metadata loading until the panel is visible */
  enabled?: boolean;
}

const EDIT_OFFERED_TYPES_VALUE = '__edit_offered_types__';

export default function EncounterEditFields({
  appointmentTypeId,
  durationMinutes,
  cptCodeId,
  initialFee = null,
  patientId,
  practitionerId,
  providerName,
  caseId,
  adminFee: existingAdminFee = 0,
  adminFeeDescription,
  isPaymentOnFile,
  paymentType,
  splitPaymentEnabled,
  onChange,
  compact = false,
  inline = false,
  paymentExtras,
  enabled = true,
}: EncounterEditFieldsProps) {
  const [editTypesOpen, setEditTypesOpen] = React.useState(false);
  const fields = useEncounterEditFields({
    initialAppointmentTypeId: appointmentTypeId ?? null,
    initialDurationMinutes: durationMinutes ?? null,
    initialCptCodeId: cptCodeId ?? null,
    initialFee,
    patientId: patientId ?? null,
    practitionerId: practitionerId ?? null,
    caseId: caseId ?? null,
    enabled,
  });

  // Notify parent of changes
  const notifyChange = React.useCallback(
    (partial: Partial<typeof fields>) => {
      const merged = { ...fields, ...partial };
      onChange?.({
        appointmentTypeId: merged.appointmentTypeId,
        durationMinutes: merged.durationMinutes,
        cptCodeId: merged.cptCodeId,
        fee: merged.fee,
        feeOverridden: merged.feeOverridden,
      });
    },
    [fields, onChange]
  );

  // Fee breakdown — coerce to number defensively (API may return strings).
  // Unknown stays null so it is never rendered as $0.
  const rawSessionFee = fields.feePreview ? fields.feePreview.calculated : initialFee;
  const sessionBaseFee =
    rawSessionFee == null || Number.isNaN(Number(rawSessionFee)) ? null : Number(rawSessionFee);
  const adminFeeVal = fields.feePreview?.admin_fee
    ? Number(fields.feePreview.admin_fee)
    : existingAdminFee;
  const adminFeeDesc =
    fields.feePreview?.admin_fee_description || adminFeeDescription || 'Materials/Admin';
  const totalFee = sessionBaseFee == null ? null : sessionBaseFee + adminFeeVal;

  const selectHeight = compact ? 32 : 36;
  const selectMinWidth = compact ? 160 : 200;

  const fontSize = compact || inline ? '0.8rem' : '0.875rem';

  const rootSx = inline
    ? { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 0.75 }
    : { display: 'flex', flexDirection: 'column', gap: compact ? 0.75 : 1 };
  const fieldRowSx = inline
    ? { display: 'flex', alignItems: 'center', gap: 0.5 }
    : { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
  const labelSx = inline ? { minWidth: 'auto', whiteSpace: 'nowrap' } : { minWidth: 80 };

  // Group types by granular category for a scannable dropdown, matching the
  // appointment/encounter dialog's Type select.
  const appointmentTypes = fields.appointmentTypes;
  const groupedTypes = React.useMemo(() => {
    const groups: Record<string, typeof appointmentTypes> = {};
    for (const t of appointmentTypes) {
      const cat = t.category || 'other';
      (groups[cat] = groups[cat] || []).push(t);
    }
    return Object.entries(groups).sort(
      ([a], [b]) => granularCategoryOrder(a) - granularCategoryOrder(b)
    );
  }, [appointmentTypes]);

  return (
    <Box sx={rootSx}>
      {/* Appointment Type */}
      <Box sx={fieldRowSx}>
        <Typography variant="caption" color="text.secondary" sx={labelSx}>
          Type
        </Typography>
        <Select
          value={fields.appointmentTypeId || ''}
          onChange={(e: SelectChangeEvent) => {
            const val = e.target.value;
            if (val === EDIT_OFFERED_TYPES_VALUE) {
              setEditTypesOpen(true);
              return;
            }
            const next = val || null;
            fields.setAppointmentTypeId(next);
            notifyChange({ appointmentTypeId: next });
          }}
          size="small"
          displayEmpty
          sx={{ minWidth: inline ? 140 : selectMinWidth, maxWidth: inline ? 220 : undefined, fontSize, height: selectHeight }}
        >
          <MenuItem value="" disabled>
            <em>Select type</em>
          </MenuItem>
          {groupedTypes.length > 1
            ? groupedTypes.map(([cat, types]) => [
                <ListSubheader
                  key={`header-${cat}`}
                  sx={{
                    fontWeight: 700,
                    fontSize,
                    lineHeight: '2rem',
                    color: 'text.primary',
                    bgcolor: 'grey.100',
                    pointerEvents: 'none',
                  }}
                >
                  {granularCategoryLabel(cat)}
                </ListSubheader>,
                ...types.map((t) => (
                  <MenuItem key={t.id} value={t.id} sx={{ pl: 4, fontSize }}>
                    {t.name}
                  </MenuItem>
                )),
              ])
            : fields.appointmentTypes.map((t) => (
                <MenuItem key={t.id} value={t.id} sx={{ fontSize }}>
                  {t.name}
                </MenuItem>
              ))}
          <Divider sx={{ my: 0.5 }} />
          <MenuItem value={EDIT_OFFERED_TYPES_VALUE} sx={{ fontSize, color: 'primary.main' }}>
            <EditOutlinedIcon sx={{ fontSize: 15, mr: 0.75 }} />
            Edit offered appointment types
          </MenuItem>
        </Select>
      </Box>

      {/* Duration (number input, not dropdown) */}
      <Box sx={fieldRowSx}>
        <Typography variant="caption" color="text.secondary" sx={labelSx}>
          Duration
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            type="number"
            size="small"
            value={fields.durationMinutes ?? ''}
            onChange={(e) => {
              const val = e.target.value ? Number(e.target.value) : null;
              fields.setDurationMinutes(val);
              notifyChange({ durationMinutes: val });
            }}
            inputProps={{ min: 1, max: 480, step: 1 }}
            sx={{ width: 72, '& input': { fontSize, py: 0.5, textAlign: 'right' } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize }}>
            min
          </Typography>
        </Box>
      </Box>

      {/* CPT Code */}
      <Box sx={fieldRowSx}>
        <Typography variant="caption" color="text.secondary" sx={labelSx}>
          CPT Code
        </Typography>
        <Select
          value={fields.cptCodeId || ''}
          onChange={(e: SelectChangeEvent) => {
            const val = e.target.value || null;
            fields.setCptCodeId(val);
            notifyChange({ cptCodeId: val });
          }}
          size="small"
          displayEmpty
          renderValue={(selected) => {
            if (!selected) return <em>None</em>;
            const cpt = fields.cptCodes.find((c) => c.id === selected);
            return cpt?.code || '';
          }}
          sx={{ minWidth: inline ? 140 : selectMinWidth, maxWidth: inline ? 220 : undefined, fontSize, height: selectHeight }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {fields.cptCodes.map((c) => (
            <MenuItem key={c.id} value={c.id} sx={{ fontSize }}>
              {c.code} &mdash; {c.description}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Fee (editable) */}
      <Box sx={fieldRowSx}>
        <Typography variant="caption" color="text.secondary" sx={labelSx}>
          Fee
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {fields.feePreviewLoading ? (
            <CircularProgress size={14} />
          ) : (
            <TextField
              type="number"
              size="small"
              value={fields.fee ?? ''}
              onChange={(e) => {
                const val = e.target.value ? Number(e.target.value) : null;
                fields.setFee(val);
                notifyChange({ fee: val, feeOverridden: true });
              }}
              InputProps={{
                startAdornment: (
                  <Box component="span" sx={{ mr: 0.5, fontSize }}>
                    $
                  </Box>
                ),
              }}
              sx={{ width: 100, '& input': { fontSize, py: 0.5 } }}
            />
          )}
        </Box>
      </Box>

      {/* Fee breakdown */}
      {sessionBaseFee == null ? (
        <Box sx={inline ? fieldRowSx : { display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.7rem' }}>
            Fee not set
          </Typography>
        </Box>
      ) : (
        (adminFeeVal > 0 || sessionBaseFee > 0) && (
          <Box sx={inline ? fieldRowSx : { display: 'flex', justifyContent: 'flex-end' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Session: ${sessionBaseFee.toFixed(2)}
              {adminFeeVal > 0 && ` + ${adminFeeDesc}: $${adminFeeVal.toFixed(2)}`}
              {' = '}
              <strong>${(totalFee ?? 0).toFixed(2)}</strong>
            </Typography>
          </Box>
        )
      )}

      {/* Payment status */}
      <Box sx={fieldRowSx}>
        <Typography variant="caption" color="text.secondary" sx={labelSx}>
          Payment
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: inline ? 'flex-start' : 'flex-end' }}>
          {isPaymentOnFile === true ? (
            <Chip
              icon={<CreditCardIcon sx={{ fontSize: 14 }} />}
              label="On File"
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          ) : isPaymentOnFile === false ? (
            <Chip
              icon={<CreditCardOffIcon sx={{ fontSize: 14 }} />}
              label="No Payment"
              size="small"
              color="error"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize }}>
              &mdash;
            </Typography>
          )}
          {splitPaymentEnabled && (
            <Chip
              icon={<CallSplitIcon sx={{ fontSize: 14 }} />}
              label="Split"
              size="small"
              color="info"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          )}
          {paymentType && paymentType !== 'self_pay' && !splitPaymentEnabled && (
            <Chip
              label={
                paymentType === 'mixed' ? 'Split Payment' : paymentType.replace('_', ' ')
              }
              size="small"
              variant="outlined"
              color={paymentType === 'mixed' ? 'error' : 'default'}
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          )}
          {paymentExtras}
        </Box>
      </Box>

      <OfferedAppointmentTypesModal
        open={editTypesOpen}
        onClose={() => setEditTypesOpen(false)}
        practitionerId={practitionerId}
        providerName={providerName}
        onSaved={fields.refreshMetadata}
      />
    </Box>
  );
}
